<template>
  <div
    v-if="show"
    class="builder-screen-v2-overlay fade-in"
    @click.self="handleClose"
  >
    <div class="builder-screen-v2">
      <!-- Top Navigation Bar -->
      <div class="builder-header">
        <button
          @click="handleBackStep"
          class="builder-nav-btn builder-nav-btn-back"
        >
          <KioskIcon
            name="back"
            size="20"
            stroke-width="2"
          />
          <span>Previous Step</span>
        </button>
        <span class="builder-title">Customize Item</span>
        <button
          @click="handleClose"
          class="builder-nav-btn builder-nav-btn-cancel"
        >
          <span>Cancel</span>
          <KioskIcon
            name="close"
            size="20"
            stroke-width="2"
          />
        </button>
      </div>

      <!-- Product Summary Header -->
      <div class="builder-product-header">
        <div class="builder-product-image">
          <img
            v-if="product.image"
            :src="product.image"
            :alt="product.name"
            class="builder-product-img"
          >
          <div
            v-else
            class="builder-product-placeholder"
          >
            <KioskIcon
              name="placeholder"
              size="32"
              stroke-width="1.5"
            />
          </div>
          <!-- Selection Overlays -->
          <div
            v-for="overlay in selectedOverlays"
            :key="'header-overlay-' + overlay.id"
            class="product-image-overlay-layer"
            :style="{ backgroundColor: overlay.color }"
          />
        </div>
        <div class="builder-product-info">
          <h2 class="builder-product-name">
            {{ getProductDisplayName(product) }}
          </h2>
          <p
            v-if="shouldShowProductDescription"
            class="builder-product-description"
          >
            {{ product.description }}
          </p>
          <div class="builder-product-summary">
            {{ getSelectedSummary() }}
          </div>
        </div>
      </div>

      <!-- Stepper Progress — hidden in vertical mode -->
      <div
        v-if="!isNoModifiers && !verticalMode"
        class="builder-stepper"
      >
        <div
          class="builder-stepper-container"
          ref="stepperContainer"
        >
          <button
            v-for="(group, idx) in groups"
            :key="group.id"
            :ref="`step-${idx}`"
            @click="goToStep(idx)"
            :disabled="idx > currentStep"
            class="builder-step-btn"
            :class="{
              active: idx === currentStep,
              completed: idx < currentStep,
              disabled: idx > currentStep,
            }"
          >
            <div class="builder-step-header">
              <span class="builder-step-number">Step {{ idx + 1 }}</span>
              <div
                v-if="isStepValid(idx) && getSelectionsCount(group.id) > 0"
                class="builder-step-check"
              >
                <KioskIcon
                  name="check"
                  size="10"
                  stroke-width="3"
                />
              </div>
            </div>
            <span class="builder-step-name">{{ group.name }}</span>
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div
        class="builder-content"
        ref="contentContainer"
      >
        <div
          v-if="isNoModifiers"
          class="builder-no-modifiers"
        >
          <div class="builder-no-modifiers-card">
            <p class="builder-no-modifiers-text">
              No customization options available for this item.
            </p>
          </div>
        </div>
        <!-- Vertical mode: all groups on one scrollable page -->
        <div
          v-else-if="verticalMode"
          class="builder-vertical-list"
        >
          <div
            v-for="group in groups"
            :key="group.id"
            class="builder-vertical-group"
          >
            <div class="builder-vertical-group-header">
              <div class="builder-vertical-group-title">
                <span class="builder-vertical-group-name">{{ group.name }}</span>
                <span class="builder-vertical-count-badge">{{ getSelectionsCount(group.id) }}</span>
              </div>
              <div class="builder-vertical-pills">
                <span
                  class="builder-vertical-pill"
                  :class="isGroupValid(group) ? 'satisfied' : (isGroupRequired(group) ? 'required' : 'optional')"
                >
                  {{ getGroupRequirementText(group) }}
                </span>
                <span class="builder-vertical-pill selection">{{ getGroupSelectionText(group) }}</span>
              </div>
            </div>

            <div class="builder-modifiers-grid">
              <button
                v-for="option in (group.options || []).filter(m => m && m.active)"
                :key="option.id"
                type="button"
                @click="toggleOption(group, option)"
                class="builder-modifier-card"
                :class="{
                  selected: isOptionSelectedForGroup(group, option),
                  'builder-modifier-card--86d': isModifierOption86d(option),
                }"
                :style="{
                  background: 'white',
                  color: '#111827',
                  borderColor: isOptionSelectedForGroup(group, option) ? '#2563EB' : '#e5e7eb',
                  borderWidth: isOptionSelectedForGroup(group, option) ? '3px' : '1px',
                }"
              >
                <div
                  class="builder-modifier-check"
                  :class="{ checked: isOptionSelectedForGroup(group, option) }"
                >
                  <KioskIcon
                    v-if="isOptionSelectedForGroup(group, option)"
                    name="check"
                    size="14"
                    stroke-width="3"
                  />
                </div>
                <div
                  v-if="option.image || option.color"
                  class="builder-modifier-visual-container"
                >
                  <div
                    class="builder-modifier-visual-wrapper"
                    :class="{ 'has-image': option.image }"
                  >
                    <img
                      v-if="option.image"
                      :src="option.image"
                      :alt="option.name"
                      class="builder-modifier-visual-img"
                    >
                    <div
                      v-if="option.color"
                      class="builder-modifier-visual-overlay"
                      :style="{
                        backgroundColor: option.color,
                        opacity: option.image ? 0.7 : 1,
                        mixBlendMode: 'normal',
                      }"
                    />
                  </div>
                </div>
                <div class="builder-modifier-content">
                  <div class="builder-modifier-name-wrapper">
                    <div class="builder-modifier-name-col">
                      <span class="builder-modifier-name">{{ option.name }}</span>
                      <span
                        v-if="isModifierOption86d(option)"
                        class="builder-modifier-unavailable-tag"
                      >Unavailable</span>
                    </div>
                  </div>
                  <div
                    v-if="option.labels && option.labels.length > 0"
                    class="builder-modifier-labels"
                  >
                    <span
                      v-for="(label, labelIdx) in option.labels"
                      :key="labelIdx"
                      class="builder-modifier-label"
                      :class="getLabelClass(label)"
                    >{{ label }}</span>
                  </div>
                  <span class="builder-modifier-price">
                    +{{ currency }}{{ (parseFloat(option.price) || 0).toFixed(2) }}
                  </span>
                  <div
                    v-if="option.description"
                    class="builder-modifier-description-container"
                  >
                    <p class="builder-modifier-description">
                      {{ option.description }}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Step mode (default): one group at a time -->
        <div
          v-else
          class="builder-modifiers-section"
        >
          <div class="builder-modifiers-header">
            <h3 class="builder-modifiers-title">
              Select Options
            </h3>
            <span
              class="builder-modifiers-badge"
              :class="{
                valid: isCurrentStepValid(),
                invalid: !isCurrentStepValid(),
              }"
            >
              {{ getStepRequirementText() }}
            </span>
          </div>

          <!-- Modifiers Grid -->
          <div class="builder-modifiers-grid">
            <button
              v-for="option in (currentGroup.options || []).filter(
                (m) => m && m.active,
              )"
              :key="option.id"
              type="button"
              @click="toggleOption(currentGroup, option)"
              class="builder-modifier-card"
              :class="{
                selected: isOptionSelected(option),
                'builder-modifier-card--86d': isModifierOption86d(option),
              }"
              :style="{
                background: 'white',
                color: '#111827',
                borderColor: isOptionSelected(option) ? '#2563EB' : '#e5e7eb',
                borderWidth: isOptionSelected(option) ? '3px' : '1px',
              }"
            >
              <!-- Selection Indicator -->
              <div
                class="builder-modifier-check"
                :class="{
                  checked: isOptionSelected(option),
                }"
              >
                <KioskIcon
                  v-if="isOptionSelected(option)"
                  name="check"
                  size="14"
                  stroke-width="3"
                />
              </div>

              <!-- Left Side: Visual Container -->
              <div
                v-if="option.image || option.color"
                class="builder-modifier-visual-container"
              >
                <div
                  class="builder-modifier-visual-wrapper"
                  :class="{ 'has-image': option.image }"
                >
                  <img
                    v-if="option.image"
                    :src="option.image"
                    :alt="option.name"
                    class="builder-modifier-visual-img"
                  >
                  <div
                    v-if="option.color"
                    class="builder-modifier-visual-overlay"
                    :style="{
                      backgroundColor: option.color,
                      opacity: option.image ? 0.7 : 1,
                      mixBlendMode: 'normal',
                    }"
                  />
                </div>
              </div>

              <!-- Content -->
              <div class="builder-modifier-content">
                <div class="builder-modifier-name-wrapper">
                  <div class="builder-modifier-name-col">
                    <span class="builder-modifier-name">
                      {{ option.name }}
                    </span>
                    <span
                      v-if="isModifierOption86d(option)"
                      class="builder-modifier-unavailable-tag"
                    >
                      Unavailable
                    </span>
                  </div>
                </div>

                <!-- Labels -->
                <div
                  v-if="option.labels && option.labels.length > 0"
                  class="builder-modifier-labels"
                >
                  <span
                    v-for="(label, labelIdx) in option.labels"
                    :key="labelIdx"
                    class="builder-modifier-label"
                    :class="getLabelClass(label)"
                  >
                    {{ label }}
                  </span>
                </div>

                <!-- Price -->
                <span class="builder-modifier-price">
                  +{{ currency
                  }}{{ (parseFloat(option.price) || 0).toFixed(2) }}
                </span>

                <!-- Description Container (Optional) -->
                <div
                  v-if="option.description"
                  class="builder-modifier-description-container"
                >
                  <p class="builder-modifier-description">
                    {{ option.description }}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Sticky Bottom Bar -->
      <div class="builder-footer">
        <div class="builder-footer-top">
          <div class="builder-total">
            <span class="builder-total-label">Total</span>
            <span
              class="builder-total-amount"
            >{{ currency }} {{ calculateTotal().toFixed(2) }}</span>
          </div>

          <div class="builder-quantity">
            <button
              @click="decreaseQuantity"
              :disabled="quantity === 1"
              class="builder-qty-btn"
            >
              <KioskIcon
                name="minus"
                size="20"
                stroke-width="3"
              />
            </button>
            <span class="builder-qty-value">{{ quantity }}</span>
            <button
              @click="increaseQuantity"
              class="builder-qty-btn"
            >
              <KioskIcon
                name="plus"
                size="20"
                stroke-width="3"
              />
            </button>
          </div>
        </div>

        <button
          @click="verticalMode ? handleAddToCart() : (isLastStep ? handleAddToCart() : handleNext())"
          :disabled="verticalMode ? (!isAllGroupsValid() || isAdding) : (!isCurrentStepValid() || isAdding)"
          class="builder-add-btn"
          :class="{
            enabled: verticalMode ? isAllGroupsValid() : isCurrentStepValid(),
            disabled: verticalMode ? !isAllGroupsValid() : !isCurrentStepValid(),
            adding: isAdding,
          }"
        >
          <span>{{
            (verticalMode || isLastStep) ? "Add to Order" : `Next: ${getNextStepName()}`
          }}</span>
          <KioskIcon
            v-if="verticalMode || isLastStep"
            name="plus"
            size="24"
            stroke-width="3"
          />
          <KioskIcon
            v-else
            name="chevronRight"
            size="24"
            stroke-width="3"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { shouldShowProductDescription } from "@/helpers/productDescriptionHelper";
import { getReadableTextColor } from "@/helpers/ui/textColor";
import KioskIcon from "./components/KioskIcon.vue";
import { mapGetters } from "vuex";

export default {
  name: "BuilderScreenV2",
  components: {
    KioskIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: null,
    },
    currency: {
      type: String,
      default: "$",
    },
    verticalMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStep: 0,
      quantity: 1,
      selections: {},
      isAdding: false,
    };
  },
  computed: {
    ...mapGetters({
      isModifier86d: "products86d/isModifier86d",
    }),
    groups() {
      if (!this.product || !this.product.modifier_groups) return [];
      return this.product.modifier_groups
        .filter((g) => {
          if (!g) return false;
          // Check if it has modifiers array (the standard format in biyowebpos)
          const hasModifiers =
            g.modifiers && Array.isArray(g.modifiers) && g.modifiers.length > 0;
          return hasModifiers;
        })
        .map((g) => {
          // Normalize: ensure options array exists (BuilderScreen expects 'options')
          if (!g.options && g.modifiers) {
            return { ...g, options: g.modifiers };
          }
          return g;
        });
    },
    isNoModifiers() {
      return this.groups.length === 0;
    },
    currentGroup() {
      if (this.isNoModifiers) return null;
      const group = this.groups[this.currentStep];
      if (!group) return null;
      // Ensure options array exists
      if (!group.options) {
        group.options = group.modifiers || [];
      }
      return group;
    },
    isLastStep() {
      return this.isNoModifiers || this.currentStep === this.groups.length - 1;
    },
    shouldShowProductDescription() {
      /**
       * Check if product description should be shown
       * Don't show if description contains "type": "alert"
       */
      if (!this.product) return false;
      return shouldShowProductDescription(this.product.description);
    },
    selectedOverlays() {
      const overlays = [];
      Object.keys(this.selections).forEach((groupId) => {
        const selectedOptions = this.selections[groupId] || [];
        selectedOptions.forEach((option) => {
          if (option.color) {
            overlays.push({
              id: option.id,
              color: option.color,
            });
          }
        });
      });
      return overlays;
    },
  },
  watch: {
    show(newVal) {
      if (newVal && this.product) {
        this.reset();
      }
    },
    currentStep() {
      this.$nextTick(() => {
        this.scrollToActiveStep();
        this.scrollContentToTop();
      });
    },
  },
  methods: {
    reset() {
      this.currentStep = 0;
      this.quantity = 1;
      const defaults = {};
      if (this.product && this.product.modifier_groups) {
        for (const group of this.product.modifier_groups) {
          const defaultMods = (group.modifiers || []).filter(m => m.is_default);
          if (defaultMods.length > 0) {
            defaults[group.id] = defaultMods;
          }
        }
      }
      this.selections = defaults;
    },
    getProductDisplayName(product) {
      // Use the inherited method from StandardOrder
      return this.$parent.getProductDisplayName
        ? this.$parent.getProductDisplayName(product)
        : product.name;
    },
    isModifierOption86d(option) {
      if (!this.product || !option) return false;
      return this.isModifier86d(this.product.id, option.id);
    },
    toggleOption(group, option) {
      if (!option) return;
      if (!group || !group.id) return;
      if (this.isModifierOption86d(option)) {
        this.$bvModal.msgBoxOk(
          "This option is not available right now.",
          {
            title: "Unavailable",
            okVariant: "secondary",
            centered: true,
            size: "sm",
          },
        );
        return;
      }

      const groupSelections = this.selections[group.id] || [];
      const isSelected = groupSelections.some((o) => o && o.id === option.id);

      let newSelections = [...groupSelections];

      const maxSelection = group.max_selection || group.maxSelection || 999;
      if (maxSelection === 1) {
        newSelections = [option];
      } else {
        if (isSelected) {
          newSelections = newSelections.filter((o) => o && o.id !== option.id);
        } else {
          if (newSelections.length < maxSelection) {
            newSelections.push(option);
          }
        }
      }

      this.$set(this.selections, group.id, newSelections);
    },
    isOptionSelected(option) {
      if (!this.currentGroup || !option || !option.id) return false;
      const groupSelections = this.selections[this.currentGroup.id] || [];
      return groupSelections.some((o) => o && o.id === option.id);
    },
    isOptionSelectedForGroup(group, option) {
      if (!group || !option || !option.id) return false;
      const groupSelections = this.selections[group.id] || [];
      return groupSelections.some((o) => o && o.id === option.id);
    },
    isGroupRequired(group) {
      return (group.min_selection || group.minSelection || 0) > 0;
    },
    isGroupValid(group) {
      const count = this.getSelectionsCount(group.id);
      const min = group.min_selection || group.minSelection || 0;
      return count >= min;
    },
    isAllGroupsValid() {
      return this.groups.every((g) => this.isGroupValid(g));
    },
    getGroupRequirementText(group) {
      if (!this.isGroupRequired(group)) return "Optional";
      return this.isGroupValid(group) ? "Requirement Met" : `Required: Select ${group.min_selection || group.minSelection}`;
    },
    getGroupSelectionText(group) {
      const min = group.min_selection || group.minSelection || 0;
      const max = group.max_selection || group.maxSelection || 0;
      if (min > 0 && max > 0) {
        return min === max ? `Select exactly ${min}` : `Select ${min}–${max}`;
      }
      if (min > 0) return `Select at least ${min}`;
      if (max > 0) return `Select up to ${max}`;
      return "Select any";
    },
    getSelectionsCount(groupId) {
      return (this.selections[groupId] || []).length;
    },
    isStepValid(stepIndex) {
      if (this.isNoModifiers) return true;
      const group = this.groups[stepIndex];
      if (!group) return true;
      const currentSelections = this.selections[group.id] || [];
      const minSelection = group.min_selection || group.minSelection || 0;
      return currentSelections.length >= minSelection;
    },
    isCurrentStepValid() {
      return this.isStepValid(this.currentStep);
    },
    goToStep(stepIndex) {
      if (stepIndex <= this.currentStep || stepIndex < this.currentStep) {
        this.currentStep = stepIndex;
      }
    },
    handleNext() {
      if (this.isCurrentStepValid() && !this.isLastStep) {
        this.currentStep++;
      }
    },
    handleBackStep() {
      if (this.verticalMode) {
        this.handleClose();
        return;
      }
      if (this.currentStep > 0) {
        this.currentStep--;
      } else {
        this.handleClose();
      }
    },
    handleClose() {
      this.$emit("close");
    },
    increaseQuantity() {
      this.quantity++;
    },
    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    calculateTotal() {
      let modsTotal = 0;
      Object.values(this.selections).forEach((opts) => {
        if (Array.isArray(opts)) {
          opts.forEach((o) => {
            if (o && o.price) {
              modsTotal += parseFloat(o.price || 0);
            }
          });
        }
      });
      return (parseFloat(this.product.price || 0) + modsTotal) * this.quantity;
    },
    getSelectedSummary() {
      const items = [];
      this.groups.forEach((g) => {
        if (!g || !g.id) return;
        const sels = this.selections[g.id] || [];
        if (sels.length > 0) {
          const names = sels.filter((s) => s && s.name).map((s) => s.name);
          if (names.length > 0) {
            items.push(names.join(", "));
          }
        }
      });
      if (items.length === 0) return "No additions yet";
      return items.join(" • ");
    },
    getStepRequirementText() {
      if (!this.currentGroup) return "Optional";
      const minSelection =
        this.currentGroup.min_selection || this.currentGroup.minSelection || 0;
      if (minSelection > 0) {
        return this.isCurrentStepValid()
          ? "Requirement Met"
          : `Required: Select ${minSelection}`;
      }
      return "Optional";
    },
    getNextStepName() {
      if (this.currentStep + 1 < this.groups.length) {
        return this.groups[this.currentStep + 1].name;
      }
      return "Review";
    },
    getLabelClass(label) {
      const text = label.toLowerCase();
      if (text.includes("spicy") || text.includes("hot")) return "spicy";
      if (text.includes("nut") || text.includes("allergy")) return "allergy";
      if (
        text.includes("gluten") ||
        text.includes("gf") ||
        text.includes("vegan")
      )
        return "dietary";
      return "default";
    },
    scrollToActiveStep() {
      if (
        this.$refs.stepperContainer &&
        this.$refs[`step-${this.currentStep}`]
      ) {
        const stepEl = this.$refs[`step-${this.currentStep}`];
        if (stepEl && stepEl[0]) {
          stepEl[0].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    },
    scrollContentToTop() {
      if (this.$refs.contentContainer) {
        this.$refs.contentContainer.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    handleAddToCart() {
      if (!this.isCurrentStepValid()) return;

      // Validate all required groups
      const allValid = this.groups.every((g) => {
        const count = this.getSelectionsCount(g.id);
        const minSelection = g.min_selection || g.minSelection || 0;
        return count >= minSelection;
      });

      if (!allValid && !this.isNoModifiers) return;

      this.isAdding = true;

      // Emit event with product, selections, and quantity
      this.$emit("add-to-cart", {
        product: this.product,
        selections: this.selections,
        quantity: this.quantity,
      });

      // Small delay for animation before closing
      setTimeout(() => {
        this.isAdding = false;
        this.handleClose();
      }, 400);
    },
    getTextColor(hex) {
      // Pure helper extracted to helpers/ui/textColor (Step 2).
      return getReadableTextColor(hex);
    },
  },
};
</script>

<style scoped>
.builder-screen-v2-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.fade-in {
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.builder-screen-v2 {
  background: white;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.builder-header {
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  flex-shrink: 0;
}

.builder-nav-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 9999px;
  font-weight: 500;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.builder-nav-btn-back {
  color: #374151;
  background: transparent;
}

.builder-nav-btn-back:hover {
  background: #f3f4f6;
}

.builder-nav-btn-back:active {
  background: #e5e7eb;
}

.builder-title {
  font-weight: bold;
  font-size: 18px;
  color: #111827;
}

.builder-nav-btn-cancel {
  color: #6b7280;
  background: transparent;
}

.builder-nav-btn-cancel:hover {
  color: #ef4444;
  background: #fef2f2;
}

.builder-nav-btn-cancel:active {
  background: #fee2e2;
}

/* Product Header */
.builder-product-header {
  background: white;
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;
}

.builder-product-image {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
  position: relative;
}

.builder-product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image-overlay-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.7;
  mix-blend-mode: normal;
  pointer-events: none;
}

.builder-product-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.builder-product-placeholder svg {
  width: 32px;
  height: 32px;
}

.builder-product-info {
  flex: 1;
  min-width: 0;
}

.builder-product-name {
  font-size: 18px;
  font-weight: bold;
  color: #111827;
  line-height: 1.4;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.builder-product-description {
  font-size: 14px;
  color: #6b7280;
  margin: 4px 0 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.builder-product-summary {
  font-size: 12px;
  color: #2563eb;
  font-weight: 500;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Stepper */
.builder-stepper {
  background: white;
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.builder-stepper::-webkit-scrollbar {
  display: none;
}

.builder-stepper-container {
  display: flex;
  gap: 12px;
  min-width: min-content;
  scroll-behavior: smooth;
}

.builder-step-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid;
  min-width: 120px;
  height: 80px;
  transition: all 0.2s;
  cursor: pointer;
  background: white;
  text-align: left;
}

.builder-step-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
}

.builder-step-btn.completed {
  border-color: #e5e7eb;
  background: white;
  color: #4b5563;
}

.builder-step-btn.completed:hover {
  border-color: rgba(37, 99, 235, 0.5);
}

.builder-step-btn.disabled {
  border-color: #f3f4f6;
  background: #f9fafb;
  color: #9ca3af;
  opacity: 0.7;
  cursor: not-allowed;
}

.builder-step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 4px;
}

.builder-step-number {
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
}

.builder-step-btn.active .builder-step-number {
  color: #2563eb;
}

.builder-step-check {
  width: 16px;
  height: 16px;
  background: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.builder-step-name {
  font-size: 14px;
  font-weight: bold;
  line-height: 1.2;
  color: #111827;
}

.builder-step-btn.disabled .builder-step-name {
  color: #9ca3af;
}

/* Content */
.builder-content {
  flex: 1;
  overflow-y: auto;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
}

.builder-no-modifiers {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  text-align: center;
}

.builder-no-modifiers-card {
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #f3f4f6;
  width: 100%;
}

.builder-no-modifiers-text {
  color: #6b7280;
  margin-bottom: 16px;
}

.builder-modifiers-section {
  padding: 24px;
  padding-bottom: 24px;
}

.builder-modifiers-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.builder-modifiers-title {
  font-size: 20px;
  font-weight: bold;
  color: #111827;
  margin: 0;
}

.builder-modifiers-badge {
  font-size: 12px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
}

.builder-modifiers-badge.valid {
  background: #dbeafe;
  color: #1e40af;
}

.builder-modifiers-badge.invalid {
  background: #e5e7eb;
  color: #6b7280;
}

/* Modifiers Grid */
.builder-modifiers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.builder-modifier-card {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  height: auto;
  min-height: 90px;
  border-radius: 12px;
  padding: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  overflow: hidden;
  position: relative;
  text-align: left;
  cursor: pointer;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.builder-modifier-card:hover {
  border-color: #d1d5db;
}

.builder-modifier-card:active {
  transform: scale(0.98);
}

.builder-modifier-card.selected {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.builder-modifier-card.builder-modifier-card--86d {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: auto;
}

.builder-modifier-card.builder-modifier-card--86d:hover {
  border-color: #e5e7eb;
}

.builder-modifier-check {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: white;
}

.builder-modifier-check.checked {
  border-color: #2563eb;
  background: #2563eb;
  color: white;
}

.builder-modifier-visual-container {
  flex-shrink: 0;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.builder-modifier-visual-wrapper {
  width: 5rem;
  height: 5rem;
  border-radius: 10px;
  background: #f3f4f6;
  position: relative;
  overflow: hidden;
  border: 1px solid #f3f4f6;
}

.builder-modifier-visual-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.builder-modifier-visual-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.builder-modifier-visual-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;
}

.builder-modifier-visual-placeholder svg {
  width: 24px;
  height: 24px;
}

.builder-modifier-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 24px;
}

.builder-modifier-name-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  margin-bottom: 2px;
}

.builder-modifier-name-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  width: 100%;
}

.builder-modifier-unavailable-tag {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 6px;
  border-radius: 4px;
  color: #991b1b;
  background: #fef2f2;
  border: 1px solid #fecaca;
  line-height: 1.2;
}

.builder-modifier-name {
  font-weight: bold;
  font-size: 14px;
  line-height: 1.3;
  color: #111827;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.builder-modifier-name.selected {
  color: #2563eb;
}

.builder-modifier-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 6px 0 4px 0;
}

.builder-modifier-label {
  font-size: 9px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid;
}

.builder-modifier-label.spicy {
  background: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}

.builder-modifier-label.allergy {
  background: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}

.builder-modifier-label.dietary {
  background: #f0fdf4;
  color: #166534;
  border-color: #bbf7d0;
}

.builder-modifier-label.default {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}

.builder-modifier-price {
  font-weight: bold;
  font-size: 12px;
  color: #374151;
  margin-top: 4px;
}

.builder-modifier-description-container {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.builder-modifier-description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Footer */
.builder-footer {
  background: white;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.08);
  padding: 24px;
  z-index: 30;
  border-top: 1px solid #f9fafb;
  flex-shrink: 0;
}

.builder-footer-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.builder-total {
  display: flex;
  flex-direction: column;
}

.builder-total-label {
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 4px;
}

.builder-total-amount {
  font-size: 32px;
  font-weight: bold;
  color: #0f172a;
  line-height: 1;
}

.builder-quantity {
  display: flex;
  align-items: center;
  background: #f3f4f6;
  border-radius: 12px;
  padding: 4px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.builder-qty-btn {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.builder-qty-btn:hover:not(:disabled) {
  background: #f9fafb;
  transform: scale(0.95);
}

.builder-qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.builder-qty-value {
  width: 56px;
  text-align: center;
  font-weight: bold;
  font-size: 20px;
  color: #111827;
}

.builder-add-btn {
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.builder-add-btn:active {
  transform: scale(0.98);
}

.builder-add-btn.enabled {
  background: #2563eb;
  color: white;
}

.builder-add-btn.adding {
  background: #10b981;
  transform: scale(0.95);
}

.builder-add-btn.enabled:hover {
  background: #1d4ed8;
}

.builder-add-btn.disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

/* Vertical mode styles */
.builder-vertical-list {
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.builder-vertical-group {
  padding-bottom: 28px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.builder-vertical-group:last-child {
  border-bottom: none;
}

.builder-vertical-group-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.builder-vertical-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.builder-vertical-group-name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.builder-vertical-count-badge {
  background: #2563eb;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
}

.builder-vertical-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.builder-vertical-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.builder-vertical-pill.required {
  background: #fee2e2;
  color: #991b1b;
}

.builder-vertical-pill.satisfied {
  background: #d1fae5;
  color: #065f46;
}

.builder-vertical-pill.optional {
  background: #f3f4f6;
  color: #4b5563;
}

.builder-vertical-pill.selection {
  background: #f3f4f6;
  color: #4b5563;
}
</style>
